import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchLocationsHistory } from 'commons/src/actions/LocationActions';
import Error from 'commons/src/components/errorComponents/Error';
import { userIsHbs } from 'commons/src/components/findUserType';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import { businessPaths, dateFormats } from 'commons/src/constants';
import DevicesList from 'commons/src/features/devices/LocationSection/DevicesList';
import { DeviceType, EndedSegment, Group, HubData, LocationType } from 'commons/src/models/commonTypeScript';
import { Store } from 'commons/src/reducers';

export type Props = {
    loading: boolean;
    locations: LocationType[];
    devices: { [key: string]: DeviceType };
    error: boolean;
    dateFormat: keyof typeof dateFormats;
    endedMeasurements: EndedSegment[];
    hubs: HubData[];
    groups: Group[];
};

export const Devices = ({
    locations,
    loading,
    devices,
    error,
    dateFormat,
    endedMeasurements,
    hubs,
    groups,
}: Props): React.ReactElement => {
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const dispatch = useDispatch();
    const { t: txt } = useTranslation();
    const [searchText, setSearchText] = useState('');

    const businessUser = userIsHbs();

    useEffect((): void => {
        if (!businessUser && groups.length > 0) {
            dispatch(fetchLocationsHistory());
        }
    }, [groups]);

    if (businessUser) {
        navigate(`/${businessPaths.buildings}`);
    }

    if (error) {
        return <Error />;
    }

    const onSearchUpdate = (text: string): void => {
        const input = text.toLowerCase();
        setSearchText(input);
    };

    return (
        <div>
            <PageHeader title={txt('AllDevices')} />
            <div className="page-wrapper">
                <SubHeader onSearchUpdate={onSearchUpdate} actionButtons={[]} />
            </div>
            <DevicesList
                locationQuery={search.toString()}
                loading={loading}
                devicesLoading={loading}
                locations={locations}
                devices={devices}
                error={error}
                dateFormat={dateFormat}
                endedMeasurements={endedMeasurements}
                hubs={hubs}
                showLocationHeader
                searchText={searchText}
            />
        </div>
    );
};

const mapStateToProps = (state: Store): Props => {
    const {
        devices: { devices, endedMeasurements, hubs },
        locations: { loading: locationsLoading, locations, error },
        userSettings: { dateFormat, groups, loading: loadingUser },
        app: { error: appError },
    } = state;

    return {
        endedMeasurements,
        devices,
        loading: locationsLoading || loadingUser,
        hubs,
        locations,
        error: error || appError,
        dateFormat,
        groups,
    };
};

export default connect(mapStateToProps)(Devices);
